import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { WHATSAPP_URL } from "../../lib/contact";
import logoMark from "../../assets/images/logo-mark.png";

const links = [
  { label: "Inicio",    href: "#inicio" },
  { label: "Nosotros",  href: "#nosotros" },
  { label: "Servicios", href: "#servicios" },
  { label: "Proceso",   href: "#proceso" },
  { label: "Contacto",  href: "#contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("#inicio");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const sections = links.map(l => document.querySelector(l.href)).filter(Boolean);
      const y = window.scrollY + 120;
      let current = "#inicio";
      sections.forEach(s => {
        if (s.offsetTop <= y) current = `#${s.id}`;
      });
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkBase = "relative font-inter text-sm tracking-wide transition-colors";

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-white/90 shadow-[0_4px_24px_-12px_rgba(27,42,74,0.20)] border-b border-brand-navy/5"
          : "backdrop-blur-sm bg-white/70"
      }`}
    >
      <nav className="mx-auto flex h-24 max-w-7xl items-center justify-between px-5 sm:px-8">
        {/* Logo */}
        <a href="#inicio" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src={logoMark}
            alt="Rodríguez Valuación y DRO"
            className="h-16 sm:h-20 w-auto object-contain"
            loading="eager"
            width="80"
            height="80"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-playfair text-lg text-brand-navy">
              Rodríguez <span className="text-brand-gold">·</span> Valuación
            </div>
            <div className="font-mont text-[10px] uppercase tracking-[0.22em] text-brand-mute">
              & DRO &nbsp;·&nbsp; Puebla
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-9">
          {links.map(l => {
            const isActive = active === l.href;
            return (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`${linkBase} ${isActive ? "text-brand-navy" : "text-brand-ink/70 hover:text-brand-navy"}`}
                >
                  {l.label}
                  <span
                    className={`absolute left-0 -bottom-1.5 h-[2px] bg-brand-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </a>
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="navbar-cta"
          className="hidden lg:inline-flex items-center gap-2 rounded-sm bg-brand-navy px-5 py-2.5 font-mont text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold border border-brand-navy hover:bg-brand-gold hover:text-brand-navy hover:border-brand-gold transition-all duration-300"
        >
          Solicitar Cotización
        </a>

        {/* Mobile toggle */}
        <button
          aria-label="Abrir menú"
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen(o => !o)}
          className="lg:hidden flex h-10 w-10 items-center justify-center rounded-sm text-brand-navy hover:bg-brand-navy hover:text-brand-gold transition-colors"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-white border-t border-brand-navy/10"
          >
            <ul className="flex flex-col px-6 py-4">
              {links.map(l => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="block py-3 font-inter text-brand-navy border-b border-brand-navy/5 last:border-0"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="pt-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-sm bg-brand-navy px-5 py-3 font-mont text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold"
                >
                  Solicitar Cotización
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
